<template>
  <div id="unauthorized">
    <div class="container-fluid pt-4 pb-4">
      <div class="section-title pt-0">
        Narrato Sync
      </div>
      <div class="col-xs-12">
        <div class="d-flex mb-3">
          <button
            type="button"
            class="btn btn-primary mr-2"
            :disabled="syncing || refreshing || loading"
            @click="runSync"
            v-if="canSync && hasPolicyWithAnyAction('narratosync:sync')"
          >
            <template v-if="syncing">
              Syncing {{ syncables }} Tasks ...
            </template>
            <template v-else>
              Sync {{ syncables }} Tasks
            </template>
          </button>
          <button
            type="button"
            class="btn btn-info mr-2"
            :disabled="syncing || refreshing || loading"
            @click="toggleSyncOptions"
            v-if="canSync && hasPolicyWithAnyAction('narratosync:sync')"
          >
            Sync Options
          </button>
          <button
            type="button"
            class="btn btn-outline-primary mr-2"
            :disabled="refreshing || syncing || loading"
            @click="refresh"
          >
            <template v-if="refreshing">
              Refreshing ...
            </template>
            <template v-else>
              Refresh
            </template>
          </button>
          <a
            href="https://thinkspain.narrato.io/"
            target="_blank"
            class="btn btn-outline-success"
            :disabled="syncing || loading"
          >Open Narrato</a>
        </div>
        <p
          v-if="syncing"
          class="text-danger"
        >
          Syncing {{ syncables }} Tasks. Do not close/refresh this page while a sync is running...
        </p>
        <div v-if="syncError && syncError.length > 0">
          <p class="alert alert-info">
            If you're encountering errors it's likely the sync process is already running.
            You can check this by looking at the latest row in the table below to see if it has a valid "Finished At" date/time.
          </p>
          <p class="alert alert-danger">
            <span class="font-weight-bold">Error:</span> {{ syncError }}
          </p>
        </div>
        <div
          v-show="syncing"
          class="loading-bar mt-2 mb-2"
        />
        <div
          v-if="showSyncOptions"
          class="row mb-4"
        >
          <div class="col">
            <div class=" border border-primary py-2 px-3">
              <p class="text-muted mb-1">
                Sync Options
              </p>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="checkbox"
                  v-model="syncOptions.skipImages"
                  id="optionSkipImages"
                >
                <label
                  class="form-check-label"
                  for="optionSkipImages"
                >
                  Skip Images
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="checkbox"
                  v-model="syncOptions.skipAudio"
                  id="optionSkipAudio"
                >
                <label
                  class="form-check-label"
                  for="optionSkipAudio"
                >
                  Skip Audio
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="checkbox"
                  v-model="syncOptions.skipStatusUpdate"
                  id="optionSkipStatus"
                >
                <label
                  class="form-check-label"
                  for="optionSkipStatus"
                >
                  Do not update status
                </label>
              </div>
              <div class="alert alert-warning mt-3 mb-0 px-2 py-2">
                To apply these options keep this panel open. Options are reset when closed.
              </div>
            </div>
          </div>
        </div>
        <div :class="{'show-loading-spinner': refreshing}">
          <div :class="{'disabled': refreshing}">
            <p>Task Sync Records</p>
            <table
              class="table table-responsive table-no-wrap"
              style="max-height: 50vh;"
            >
              <thead>
                <tr>
                  <th>
                    ID
                  </th>
                  <th>
                    Started At
                  </th>
                  <th>
                    Finished At
                  </th>
                  <th>
                    Status
                  </th>
                  <th>
                    Error
                  </th>
                  <th>
                    Tasks Loaded
                  </th>
                  <th>
                    New Translation (Existing Article)
                  </th>
                  <th>
                    New Translation, New Article
                  </th>
                  <th>
                    Existing
                  </th>
                  <th>
                    Images Added
                  </th>
                  <th>
                    Images Updated
                  </th>
                  <th>
                    Synthesis Tasks
                  </th>
                  <th>
                    Raw Result
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="record in records"
                  :key="record.id"
                >
                  <td>{{ record.id }}</td>
                  <td class="text-nowrap">
                    {{ new Date( record.startedAt ).toLocaleDateString() }} @ {{ new Date( record.startedAt ).toLocaleTimeString() }}
                  </td>
                  <td class="text-nowrap">
                    <template v-if="record.finishedAt">
                      {{ new Date( record.finishedAt ).toLocaleDateString() }} @ {{ new Date( record.finishedAt ).toLocaleTimeString() }}
                    </template>
                    <template v-else>
                      <i>N/A</i>
                    </template>
                  </td>
                  <td><span :class="{'text-capitalize':true,'font-weight-bold': true,'text-info': record.result.status === 'started','text-danger': record.result.status === 'failed','text-success': record.result.status === 'completed',}">{{ record.result.status || 'N/A' }}</span></td>
                  <td class="text-nowrap">{{ record.result.error || 'N/A' }}</td>
                  <td>{{ record.result.loaded || 0 }}</td>
                  <td>{{ record.result.newTaskExistingArticle || 0 }}</td>
                  <td>{{ record.result.newTaskNewArticle || 0 }}</td>
                  <td>{{ record.result.existingTask || 0 }}</td>
                  <td>{{ record.result.imagesAdded || 0 }}</td>
                  <td>{{ record.result.imagesUpdated || 0 }}</td>
                  <td>{{ record.result.synthesisTasksStarted || 0 }}</td>
                  <td>
                    <div class="text-nowrap">
                      {{ record.result || 'N/A' }}
                    </div>
                  </td>
                </tr>
                <tr v-if="!records">
                  <td
                    colspan="12"
                    class="text-center"
                  >
                    <span class="text-muted">No records found</span>
                  </td>
                </tr>
              </tbody>
            </table>
            <div
              v-if="records && records.length >= limit"
              class="row"
            >
              <div class="col-2">
                <div class="form-group mb-0 row align-items-center">
                  <label class="col-auto m-0">Show:</label>
                  <input
                    v-model="limit"
                    class="form-control form-control-sm col"
                    type="number"
                    :disabled="loading"
                    @change="getRecords"
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import http from '../../../http';
import notify from '../../../notifications';
import cmsAPI from '../../../cmsapi';
import { hasPolicyWithAnyAction } from '../../../auth/index';
import store from '../../../store';

export default {
    data() {
        return {
            limit: 10,
            records: [],
            loading: false,
            syncing: false,
            refreshing: false,
            syncError: '',
            canSync: false,
            syncables: 0,
            showSyncOptions: false,
            syncOptions: {
                skipImages: false,
                skipAudio: false,
                skipStatusUpdate: false
            }
        };
    },
    methods: {
        async runSync() {
            this.syncing = true;

            this.syncError = '';

            const params = new URLSearchParams();
            Object.keys( this.syncOptions ).forEach( key => params.set( key, this.syncOptions[ key ] ) );

            const response = await http.post( `narrato-sync/sync?${params.toString()}` );

            if ( !response.ok ) {
                notify( {
                    type: 'error',
                    text: 'Something went wrong',
                    title: 'Failed to run Narrato sync'
                } );

                const errResponse = await response.json();

                this.syncError = errResponse.error;

                this.syncing = false;

                return;
            }

            if ( response.status === 200 ) {
                notify( {
                    type: 'info',
                    title: 'Sync completed succesfully'
                } );
            }

            if ( response.status === 202 ) {
                notify( {
                    type: 'info',
                    title: 'Syncing is taking longer than expected',
                    text: 'It looks like the sync is taking longer than expected, refresh to check the status'
                } );
            }

            this.resetSyncOptions();

            this.showSyncOptions = false;

            this.syncing = false;

            await this.refresh();
        },
        toggleSyncOptions() {
            if ( !this.showSyncOptions ) {
                this.showSyncOptions = true;
            } else {
                this.showSyncOptions = false;
                this.resetSyncOptions();
            }
        },
        resetSyncOptions() {
            this.syncOptions.skipAudio = false;
            this.syncOptions.skipImages = false;
            this.syncOptions.skipStatusUpdate = false;
        },
        async refresh() {
            this.refreshing = true;

            await this.getRecords();

            await this.checkSyncables();

            this.refreshing = false;
        },
        async checkSyncables() {
            const response = await cmsAPI.get( 'narratoSync/countSyncables' );

            if ( response.status !== 200 ) {
                notify( {
                    type: 'error',
                    text: 'Something went wrong',
                    title: 'Failed to get syncable task count'
                } );
                this.loading = false;
                return;
            }

            const data = await response.json();

            this.syncables = data.total || 0;
            this.canSync = data.total && data.total > 0;
        },
        async getRecords() {
            const response = await cmsAPI.get( `narratoSync/records?limit=${this.limit}` );

            if ( response.status !== 200 ) {
                notify( {
                    type: 'error',
                    text: 'Something went wrong',
                    title: 'Failed fetching sync records'
                } );
                this.loading = false;
                return;
            }

            const records = await response.json();

            this.records = records;
        },
        hasPolicyWithAnyAction( ...actionPrefix ) {
            return hasPolicyWithAnyAction( store.state.user, ...actionPrefix );
        }
    },
    async mounted() {
        this.$store.dispatch( 'appLoading', true );

        try {
            await Promise.all( [
                this.getRecords(),
                this.checkSyncables()
            ] );
        } catch ( err ) {
            notify( {
                type: 'error',
                text: 'Something went wrong',
                title: 'Failed fetching sync records'
            } );
        }

        this.$store.dispatch( 'appLoading', false );

        document.title = 'thinkSPAIN - Narrato Sync';
    }
};
</script>




// WEBPACK FOOTER //
// src/components/dashboard/narratosync/Dashboard.vue